import React, { useState } from 'react';

import { ButtonBase, IconButton, Typography } from '@mui/material';
import clsx from 'clsx';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { animated, useSpring } from 'react-spring';
/*
 * Imported deeply rather than through `src/components` / `src/hooks`. Those
 * barrels reach the whole component graph, and this shell is rendered by every
 * pending-interaction card — including ones whose tests mock only a sliver of
 * Apollo.
 */
import { AlineButton } from 'src/components/generic/AlineButton';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { useAlineMeasure } from 'src/hooks/dom-hooks';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  card: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1.5),
    width: '100%',
  },
  footer: {
    alignItems: 'center',
    display: 'flex',
    flex: '0 0 auto',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'space-between',
  },
  headerActions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  headerTitle: {
    alignItems: 'center',
    color: theme.appColors.secondaryType,
    display: 'flex',
    gap: theme.spacing(0.75),
  },
  headerTitleIcon: {
    color: theme.appColors.primary,
  },
  headerTitleText: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.primary,
    fontSize: 13,
    fontWeight: 500,
  },
  pendingCard: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.appColors.rule}`,
    borderBottom: 'none',
    borderRadius: `${theme.spacing(1)} ${theme.spacing(1)} 0 0`,
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateRows: 'auto minmax(0, 1fr)',
    maxHeight: 'inherit',
    minHeight: 0,
    overflow: 'hidden',
    padding: theme.spacing(1, 2),
  },
  pendingInteractionBody: {
    minHeight: 0,
    minWidth: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    paddingRight: theme.spacing(0.5),
  },
  pendingInteractionBodyContent: {
    display: 'flow-root',
    minWidth: 0,
  },
  pendingInteractionRegion: {
    minHeight: 0,
    overflow: 'hidden',
  },
  pendingInteractionRegionInner: {
    boxSizing: 'border-box',
    display: 'grid',
    gap: theme.spacing(0.5),
    gridTemplateRows: 'minmax(0, 1fr) auto',
    height: '100%',
    minHeight: 0,
    paddingBottom: theme.spacing(0.5),
    paddingTop: theme.spacing(1),
  },
  primaryActionButton: {
    '&&': {
      borderRadius: 4,
      fontSize: 12,
      fontWeight: 600,
      height: 'unset',
      minWidth: 0,
      padding: theme.spacing(0.5, 1.5),
    },
  },
  primaryActionTooltipAnchor: {
    display: 'inline-flex',
  },
  secondaryActionButton: {
    ...getTypographyForSx(theme.typography.body2),
    '&.Mui-disabled': {
      opacity: 0.5,
    },
    alignItems: 'center',
    borderRadius: 4,
    color: theme.appColors.primary,
    display: 'flex',
    fontSize: 12,
    fontWeight: 600,
    justifyContent: 'center',
    padding: theme.spacing(0.25, 0.5),
  },
}));

function getCssPixelValue(value: string) {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return 0;
  return parsed;
}

function useAvailableCardBodyHeight(maxHeight: number | null | undefined) {
  const [card, setCardElement] = useState<HTMLDivElement | null>(null);
  const [headerElement, headerMeasure] = useAlineMeasure<HTMLDivElement>();

  if (maxHeight === null || maxHeight === undefined) {
    return { bodyMaxHeight: null, cardElement: setCardElement, headerElement };
  }

  if (!card || !headerMeasure || headerMeasure.height === 0)
    return { bodyMaxHeight: maxHeight, cardElement: setCardElement, headerElement };

  const cardStyles = window.getComputedStyle(card);
  const rowGap = getCssPixelValue(cardStyles.rowGap);
  const verticalChrome =
    headerMeasure.height +
    getCssPixelValue(cardStyles.paddingTop) +
    getCssPixelValue(cardStyles.paddingBottom) +
    getCssPixelValue(cardStyles.borderTopWidth) +
    getCssPixelValue(cardStyles.borderBottomWidth) +
    rowGap;

  return {
    bodyMaxHeight: Math.max(0, maxHeight - verticalChrome),
    cardElement: setCardElement,
    headerElement,
  };
}

/**
 * The shell every "Aline needs you" card renders inside — questions, approvals,
 * intake forms, and account connections alike.
 *
 * Sharing it is the point: these all interrupt a turn for the same reason and
 * appear in the same slot above the composer, so a card that dressed itself
 * differently would read as a different kind of thing than it is.
 */
export function PendingInteractionCardChrome({
  bodyRef,
  children,
  expanded,
  footer,
  headerActions,
  maxHeight,
  onToggleExpanded,
  title,
  toggleLabel,
}: {
  children: React.ReactNode;
  expanded: boolean;
  title: string;
  bodyRef?: React.Ref<HTMLDivElement>;
  footer?: React.ReactNode;
  headerActions?: React.ReactNode;
  maxHeight?: number | null;
  onToggleExpanded?: () => void;
  toggleLabel?: string;
}) {
  const { classes } = useStyles();
  /**
   * The card and region heights are animated. Observing either with
   * useAlineMeasure feeds every spring frame back into React and restarts it.
   */
  const [regionNode, setRegionElement] = useState<HTMLDivElement | null>(null);
  const [bodyContentElement, bodyContentMeasure] = useAlineMeasure<HTMLDivElement>();
  const [footerElement, footerMeasure, footerNode] = useAlineMeasure<HTMLDivElement>();
  const { bodyMaxHeight, cardElement, headerElement } = useAvailableCardBodyHeight(maxHeight);
  /**
   * Expanding controls can move underneath a stationary pointer. Keep them
   * non-interactive until settled so a tooltip cannot open mid-spring.
   */
  const [regionInteractive, setRegionInteractive] = useState(false);
  const regionStyles = regionNode ? window.getComputedStyle(regionNode) : null;
  const regionGap = regionStyles ? getCssPixelValue(regionStyles.rowGap) : 0;
  const regionPaddingBottom = regionStyles ? getCssPixelValue(regionStyles.paddingBottom) : 0;
  const regionPaddingTop = regionStyles ? getCssPixelValue(regionStyles.paddingTop) : 0;
  const footerHeight = footerNode ? (footerMeasure?.height ?? 0) + regionGap : 0;
  const naturalExpandedHeight =
    regionPaddingTop + (bodyContentMeasure?.height ?? 0) + footerHeight + regionPaddingBottom;
  const expandedHeight =
    bodyMaxHeight === null ? naturalExpandedHeight : Math.min(naturalExpandedHeight, bodyMaxHeight);
  const cardStyle = maxHeight ? { maxHeight } : undefined;
  const animatedRegionStyle = useSpring({
    height: expanded ? expandedHeight : 0,
    onRest: () => setRegionInteractive(expanded),
    onStart: () => setRegionInteractive(false),
    opacity: expanded ? 1 : 0,
  });

  return (
    <div ref={cardElement} className={clsx(classes.card, classes.pendingCard)} style={cardStyle}>
      <div ref={headerElement} className={classes.header}>
        <div className={classes.headerTitle}>
          <Sparkles className={classes.headerTitleIcon} size={14} />
          <Typography className={classes.headerTitleText}>{title}</Typography>
        </div>
        <div className={classes.headerActions}>
          {headerActions}
          {onToggleExpanded && toggleLabel && (
            <IconButton
              aria-expanded={expanded}
              aria-label={expanded ? `Collapse ${toggleLabel}` : `Expand ${toggleLabel}`}
              size="small"
              onClick={onToggleExpanded}
            >
              {expanded ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
            </IconButton>
          )}
        </div>
      </div>
      <animated.div
        className={classes.pendingInteractionRegion}
        style={{
          ...animatedRegionStyle,
          pointerEvents: regionInteractive ? 'auto' : 'none',
        }}
      >
        <div ref={setRegionElement} className={classes.pendingInteractionRegionInner}>
          <div ref={bodyRef} className={classes.pendingInteractionBody}>
            <div ref={bodyContentElement} className={classes.pendingInteractionBodyContent}>
              {children}
            </div>
          </div>
          {footer && (
            <div ref={footerElement} className={classes.footer}>
              {footer}
            </div>
          )}
        </div>
      </animated.div>
    </div>
  );
}

export function CardSecondaryAction({
  children,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
}) {
  const { classes } = useStyles();
  return (
    <ButtonBase className={classes.secondaryActionButton} disabled={disabled} onClick={onClick}>
      {children}
    </ButtonBase>
  );
}

export function CardPrimaryAction({
  children,
  disabled,
  disabledReason = '',
  onClick,
}: {
  children: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
  disabledReason?: string;
}) {
  const { classes } = useStyles();

  return (
    <AlineTooltip arrow placement="top" title={disabledReason}>
      <div className={classes.primaryActionTooltipAnchor}>
        <AlineButton
          buttonClassName={classes.primaryActionButton}
          color="primary"
          disabled={disabled || Boolean(disabledReason)}
          size="small"
          variant="contained"
          onClick={onClick}
        >
          {children}
        </AlineButton>
      </div>
    </AlineTooltip>
  );
}
